import { Injectable, inject } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Book_BooklistModel } from '@app/pages/booklists/models/Book_Booklist.model';
import { BooklistAggrigatorModel } from '@app/pages/booklists/models/booklist-aggrigator-model';
import { BooklistAggrigatorService } from '@app/pages/booklists/services/booklist-aggrigator.service';
import { IClubWithActiveCourseModel } from '@app/pages/clubs/models/club-with-active-course-model';
import { ClubAggregatorService } from '@app/pages/clubs/services/club-aggregator.service';
import { IPostModel } from '@app/pages/posts/models/post.model';
import { PostAggService } from '@app/pages/posts/services/postAggrigator.service';
import { PublisherAggrigatorService } from '@app/pages/publisher/services/publisher-aggrigator.service';
import { QuoteModel } from '@app/pages/quotes/models/quote-model';
import { QuoteAggrigatorService } from '@app/pages/quotes/services/quote-aggrigator.service';
import { ReviewsAggrigatorService } from '@app/pages/reviews/services/reviewsAggrigator.service';
import { UserStateService } from '@app/state/user-state.service';
import { environment } from '@environments/environment';
import { ActivityLikedUsersComponent } from '@shared/components/activity-liked-users/activity-liked-users.component';
import { SnackBarTypes } from '@shared/components/cutome-snack-bar/custome-snack-bar.component';
import { RestrictionMessageDialogComponent } from '@shared/components/dialogs/restriction-message-dialog/restriction-message-dialog/restriction-message-dialog.component';
import { LocalRepository } from '@shared/helper/local-repository';
import { TServiceResult } from '@shared/helper/t-service-result';
import { BannerModel } from '@shared/model/banner-model';
import { IGetCategoryDto, ROOT_CATEGORY_ID } from '@shared/model/category/category.model';
import { PageList } from '@shared/model/categoryAndSearch/pageList-model';
import { SearchFilterModel } from '@shared/model/categoryAndSearch/search-filter-model';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { CreateLikeModel } from '@shared/model/customerModels/like-model';
import { ReviewAggrigatorModel } from '@shared/model/review-aggrigator-model';
import { ISuggestedPublisherDto } from '@shared/model/suggested-user/suggested-user.model';
import { User } from '@shared/model/user-context/user.model';

import { BannerService } from '@shared/service/http_services/banner.service';
import { LikesService } from '@shared/service/http_services/like.service';
import { StatisticsService } from '@shared/service/http_services/statistics.service';
import { AppStateService } from '@app/state/app-state.service';
import { ViewTrackerService } from '@shared/service/view-service/view-tracker.service';
import { htmlUrls } from '@shared/utils/router-paths';
import { SnackbarService } from '@shared/utils/snackbar.service';
import { Util } from '@shared/utils/utils';
import { EXPLORE_SCROLL_POSITION_KEY } from 'constants/storage-keys';
import { BehaviorSubject, Observable, Subject, forkJoin, map, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ExploreFacadeService {
  private repo = inject(LocalRepository);
  private bannerService = inject(BannerService);
  private appState = inject(AppStateService);
  private clubAggregatorService = inject(ClubAggregatorService);
  private quoteAggrigatorService = inject(QuoteAggrigatorService);
  private reviewsAggrigatorService = inject(ReviewsAggrigatorService);
  private bookListAggrigatorService = inject(BooklistAggrigatorService);
  private publisherAggrigator = inject(PublisherAggrigatorService);
  private postAggService = inject(PostAggService);
  private utils = inject(Util);
  private _dialog = inject(MatDialog);
  private likesService = inject(LikesService);
  private _bottomSheet = inject(MatBottomSheet);
  private router = inject(Router);
  private statisticsService = inject(StatisticsService);
  private snackbarService = inject(SnackbarService);
  private viewTrackerService = inject(ViewTrackerService);
  private userStateService = inject(UserStateService);


  public currentUser!: User | null;
  public _loadingBanner = true;
  public _loadingReview = true;
  public _loadingPost = true;
  public _loadingList = true;
  public _loadingQuote = true;
  public _loadingNewBook = true;
  public _loadingPublisher = true;
  public _loadingClub = true;
  public _showResultSearch: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _banners$: BehaviorSubject<BannerModel[]> = new BehaviorSubject<BannerModel[]>([]);
  private _reviews$: BehaviorSubject<ReviewAggrigatorModel[]> = new BehaviorSubject<ReviewAggrigatorModel[]>([]);
  private _bookList$: BehaviorSubject<BooklistAggrigatorModel[]> = new BehaviorSubject<BooklistAggrigatorModel[]>([]);
  private _posts$: BehaviorSubject<IPostModel[]> = new BehaviorSubject<IPostModel[]>([]);
  private _quotes$: BehaviorSubject<QuoteModel[]> = new BehaviorSubject<QuoteModel[]>([]);
  private _clubs$: BehaviorSubject<IClubWithActiveCourseModel[]> = new BehaviorSubject<IClubWithActiveCourseModel[]>([]);
  private _newBooks$: BehaviorSubject<Book_BooklistModel[]> = new BehaviorSubject<Book_BooklistModel[]>([]);
  private _activePublisher$: BehaviorSubject<ISuggestedPublisherDto[]> = new BehaviorSubject<ISuggestedPublisherDto[]>([]);
  private _onTransitionDone$ = new Subject<void>();


  environment = environment;
  newBookListId: string = this.environment.production
    ? "5d365533-6493-4f81-bf17-4e308264da30"
    : "970b259b-fd11-4717-88cb-c6e76e9d0962";


  public get onTransitionDone$(): Observable<void> {
    return this._onTransitionDone$.asObservable();
  }

  public get banners$(): Observable<BannerModel[]> {
    return this._banners$.asObservable();
  }

  public get reviews$(): Observable<ReviewAggrigatorModel[]> {
    return this._reviews$.asObservable();
  }

  public get bookList$(): Observable<BooklistAggrigatorModel[]> {
    return this._bookList$.asObservable();
  }

  public get posts$(): Observable<IPostModel[]> {
    return this._posts$.asObservable();
  }

  public get quotes$(): Observable<QuoteModel[]> {
    return this._quotes$.asObservable();
  }

  public get clubs$(): Observable<IClubWithActiveCourseModel[]> {
    return this._clubs$.asObservable();
  }

  public get newBooks$(): Observable<Book_BooklistModel[]> {
    return this._newBooks$.asObservable();
  }

  public get activePublisher$(): Observable<ISuggestedPublisherDto[]> {
    return this._activePublisher$.asObservable();
  }

  // ? init categories
  public get categories$(): Observable<IGetCategoryDto[]> {
    return this.appState.categories$.pipe(
      map(r =>
        r?.filter(c => c.parentId === ROOT_CATEGORY_ID)
      )
    );
  }


  constructor() {
    if (this.repo.IsInBrowser) {
      this.currentUser = this.userStateService.userDataSnapshot;
    }
  }

  public loadInitialPageData(): Observable<any> {
    return forkJoin({
      banners: this._banners$.value.length < 1 ? this.getBanners() : of(this._banners$.value),
      bookList: this._bookList$.value.length < 1 ? this.getSuggestedBookLists() : of(null),
      reviews: this._reviews$.value.length < 1 ? this.getSuggestedReviews() : of(null),
      clubs: this._clubs$.value.length < 1 ? this.getSuggestedClubs() : of(null),
      posts: this._posts$.value.length < 1 ? this.getSuggestedPosts() : of(null),
      quotes: this._quotes$.value.length < 1 ? this.getSuggestedQuotes() : of(null),
      newBooks: this._newBooks$.value.length < 1 ? this.getNewBooks() : of(null),
      activePublishers: this._activePublisher$.value.length < 1 ? this.getActivePublisher() : of(null),
    })
  }

  private getBanners() {
    const bannerTermfilter: TermFilterModel = new TermFilterModel(0, 15);
    this._loadingBanner = true;
    return this.bannerService.getBanners(bannerTermfilter).pipe(
      tap(res => {
        this._loadingBanner = false;
        if (res.hasError) {
          this._banners$.next([]);
        }
        this._banners$.next(res.result.items);
      })
    );
  }

  private getSuggestedReviews(): Observable<TServiceResult<PageList<ReviewAggrigatorModel>>> {
    this._loadingReview = true;
    const reviewsTermfilter: TermFilterModel = new TermFilterModel(0, 15);
    return this.reviewsAggrigatorService.getChosenReviewsWithUserLiked(reviewsTermfilter).pipe(
      tap(res => {
        this._loadingReview = false;
        if (!res.hasError) {
          this._reviews$.next(res.result.items);
        }
        else
          this._reviews$.next([]);
      })
    );
  }

  private getSuggestedClubs(): Observable<TServiceResult<PageList<IClubWithActiveCourseModel>>> {
    this._loadingClub = true;
    const clubsTermfilter: SearchFilterModel = new SearchFilterModel(0, 15);
    return this.clubAggregatorService.getChosenClubsWithState(clubsTermfilter).pipe(
      tap(res => {
        this._loadingClub = false;
        if (!res.hasError) {
          this._clubs$.next(res.result.items);
        }
        else
          this._clubs$.next([]);
      })
    );
  }

  private getSuggestedPosts(): Observable<TServiceResult<PageList<IPostModel>>> {
    this._loadingPost = true;
    const postsTermfilter: TermFilterModel = new TermFilterModel(0, 15);
    return this.postAggService.getChosenBookListsWithUserLiked(postsTermfilter).pipe(
      tap(res => {
        this._loadingPost = false;
        if (!res.hasError) {
          this._posts$.next(res.result.items);
        }
        else
          this._posts$.next([]);
      })
    );
  }

  private getSuggestedQuotes(): Observable<TServiceResult<PageList<QuoteModel>>> {
    this._loadingQuote = true;
    const quotesTermfilter: TermFilterModel = new TermFilterModel(0, 15);
    return this.quoteAggrigatorService.getChosenQuotesWithUserLiked(quotesTermfilter).pipe(
      tap(res => {
        this._loadingQuote = false;
        if (!res.hasError) {
          this._quotes$.next(res.result.items);
        }
        else
          this._quotes$.next([]);
      })
    );
  }

  private getSuggestedBookLists(): Observable<TServiceResult<PageList<BooklistAggrigatorModel>>> {
    this._loadingList = true;
    const bookListTermfilter: TermFilterModel = new TermFilterModel(0, 15);
    return this.bookListAggrigatorService.getChosenBookListsWithUserLiked(bookListTermfilter).pipe(
      tap(res => {
        this._loadingList = false;
        if (!res.hasError) {
          this._bookList$.next(res.result.items);
        }
        else
          this._bookList$.next([]);
      })
    );
  }

  private getNewBooks(): Observable<TServiceResult<PageList<Book_BooklistModel>>> {
    this._loadingNewBook = true;
    const newBooksTermFilter: TermFilterModel = new TermFilterModel(0, 15);
    return this.bookListAggrigatorService.getBooksOfBooklist(this.newBookListId, newBooksTermFilter).pipe(
      tap(res => {
        this._loadingNewBook = false;
        if (!res.hasError) {
          this._newBooks$.next(res.result.items);
        }
        else
          this._newBooks$.next([]);
      })
    );
  }

  private getActivePublisher(): Observable<TServiceResult<PageList<ISuggestedPublisherDto>>> {
    this._loadingPublisher = true;
    return this.publisherAggrigator.getActivePublishers().pipe(
      tap(res => {
        if (!res?.hasError) {
          this._loadingPublisher = false;
          this._activePublisher$.next(res?.result?.items)
        }
        else {
          this._loadingPublisher = false;
          this._activePublisher$.next([])
        }
      })
    )
  }


  public likeItem(likedObject: CreateLikeModel): Observable<any> {
    if (!this.currentUser?.uniqeName && !this.currentUser?.firstName) {
      this.utils.opensigninDialog(this._dialog, '/', this.repo);
      return of(null);
    }
    return this.likesService.createLike(likedObject, this.currentUser.id || "").pipe(
      tap(res => {
        if (res?.hasError) {
          likedObject.userLiked = false;
          this._dialog.open(RestrictionMessageDialogComponent, {
            panelClass: 'restrict-message-dialog',
            data: likedObject
          })
        }
      }),
    );
  }

  public openUsersDialog(itemId: string) {
    this.utils.peopleLiked(
      itemId,
      this._bottomSheet,
      this._dialog,
      this.repo,
      ActivityLikedUsersComponent
    );
  }

  public navigateToCategoryBooks(cat: IGetCategoryDto) {
    this.router.navigate([htmlUrls.category], { queryParams: { id: cat.id } });
  }

  public openBannerTab(hashedLink: string): Observable<any> {
    if (this.repo.IsInBrowser) {
      return this.statisticsService.redirectToLink(hashedLink).pipe(
        tap(res => {
          if (res.hasError) {
            this.snackbarService.openSnackbar(SnackBarTypes.error, 'عملیات با خطا مواجه شد');
            return;
          }
          const url = res.result;
          window.open(url, '_blank');
        })
      );
    }
    return of(null);
  }

  public setScrollPosition(position: number): void {
    if (this.repo.IsInBrowser) {
      this.repo.setLocalItem(EXPLORE_SCROLL_POSITION_KEY, position.toString());
    }
  }

  public getScrollPosition(): number | null {
    if (this.repo.IsInBrowser) {
      return parseInt(this.repo.getLocalItem(EXPLORE_SCROLL_POSITION_KEY)!, 10) ?? null;
    }
    else return null;
  }

  public get Repository(): LocalRepository {
    return LocalRepository.Instance;
  }

  public get viewTracker() {
    return this.viewTrackerService;
  }

  public scrollToTop(): void {
    if (this.repo.IsInBrowser)
      window.scrollTo(0, 0);
  }

  public runSetTimeout() {
    this._onTransitionDone$.next();
  }

}
